import { Head } from 'vite-react-ssg';
import { absUrl } from '../lib/seo';

interface SeoProps {
  /** Full <title> text (also used for og:title / twitter:title). */
  title: string;
  /** Meta description (also used for og:description / twitter:description). */
  description: string;
  /** Root-relative path of this route, e.g. "/" or "/waitlist". Drives the canonical + og:url. */
  path: string;
  /** og:type. Defaults to "website". */
  type?: string;
  /** Absolute URL or root-relative path to the share image. Defaults to the site card. */
  image?: string;
  /** JSON-LD document(s) to emit in the <head> for this route. */
  jsonLd?: Array<Record<string, unknown>>;
}

/**
 * Per-route document head: title, description, canonical, Open Graph, Twitter Card
 * and JSON-LD. Rendered through vite-react-ssg's <Head> (a react-helmet wrapper),
 * so every tag is baked into the pre-rendered HTML at build time — no hydration or
 * backend needed for a crawler to read it.
 */
export default function Seo({
  title,
  description,
  path,
  type = 'website',
  image = '/og-image.png',
  jsonLd = [],
}: SeoProps) {
  const url = absUrl(path);
  const imageUrl = image.startsWith('http') ? image : absUrl(image);

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content="reeve" />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="reeve: an AI HR advisor for UK small businesses" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Structured data */}
      {jsonLd.map((doc, i) => (
        <script type="application/ld+json" key={i}>
          {JSON.stringify(doc)}
        </script>
      ))}
    </Head>
  );
}
